// Category Navigator - Collapsible category sidebar for Macro Monitor
import React, { useState, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  TrendingUp,
  DollarSign,
  Globe,
  Building,
  Landmark,
  Users,
  UserCircle,
  Briefcase,
  Shield,
  Search,
} from 'lucide-react';
import {
  CATEGORIES,
  getIndicatorsByCategory,
  type IndicatorCategory,
  type IndicatorMetadata,
} from '../../data/indicatorRegistry';

interface CategoryNavigatorProps {
  selectedIndicator: string | null;
  onIndicatorSelect: (indicatorId: string) => void;
  onCategorySelect?: (category: IndicatorCategory) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

// Icon mapping for categories
const getCategoryIcon = (iconName: string, size: number = 14) => {
  const iconProps = { size, className: 'shrink-0' };
  switch (iconName) {
    case 'TrendingUp':
      return <TrendingUp {...iconProps} />;
    case 'DollarSign':
      return <DollarSign {...iconProps} />;
    case 'Globe':
      return <Globe {...iconProps} />;
    case 'Building':
      return <Building {...iconProps} />;
    case 'Landmark':
      return <Landmark {...iconProps} />;
    case 'Users':
      return <Users {...iconProps} />;
    case 'UserCircle':
      return <UserCircle {...iconProps} />;
    case 'Briefcase':
      return <Briefcase {...iconProps} />;
    case 'Shield':
      return <Shield {...iconProps} />;
    default:
      return <TrendingUp {...iconProps} />;
  }
};

// Individual category accordion item
const CategoryItem: React.FC<{
  category: (typeof CATEGORIES)[0];
  indicators: IndicatorMetadata[];
  isExpanded: boolean;
  selectedIndicator: string | null;
  onToggle: () => void;
  onIndicatorSelect: (indicatorId: string) => void;
}> = ({ category, indicators, isExpanded, selectedIndicator, onToggle, onIndicatorSelect }) => {
  const primaryIndicators = indicators.filter(ind => ind.isPrimary);
  const secondaryIndicators = indicators.filter(ind => !ind.isPrimary);

  return (
    <div className="border-b border-terminal-border last:border-b-0">
      {/* Category Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-terminal-bg/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown size={12} className="text-gray-500 shrink-0" />
        ) : (
          <ChevronRight size={12} className="text-gray-500 shrink-0" />
        )}
        <span style={{ color: category.color }}>{getCategoryIcon(category.icon)}</span>
        <span className="text-[11px] font-medium text-gray-300 flex-1 text-left truncate">
          {category.name}
        </span>
        <span className="text-[9px] text-gray-600">{indicators.length}</span>
      </button>

      {/* Indicator List */}
      {isExpanded && (
        <div className="pb-1">
          {/* Primary Indicators */}
          {primaryIndicators.map(indicator => (
            <button
              key={indicator.id}
              onClick={() => onIndicatorSelect(indicator.id)}
              className={`w-full flex items-center gap-2 px-3 py-1.5 pl-7 text-left transition-colors ${
                selectedIndicator === indicator.id
                  ? 'bg-data-amber/10 border-l-2 border-data-amber'
                  : 'hover:bg-terminal-bg/50 border-l-2 border-transparent'
              }`}
            >
              <span
                className={`text-[10px] truncate ${
                  selectedIndicator === indicator.id ? 'text-data-amber' : 'text-gray-400'
                }`}
              >
                {indicator.name}
              </span>
              <span className="text-[8px] text-gray-600 shrink-0">{indicator.unit}</span>
            </button>
          ))}

          {/* Secondary Indicators (collapsed by default, show on hover) */}
          {secondaryIndicators.length > 0 && (
            <div className="mt-1 border-t border-terminal-border/50 pt-1">
              {secondaryIndicators.map(indicator => (
                <button
                  key={indicator.id}
                  onClick={() => onIndicatorSelect(indicator.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1 pl-7 text-left transition-colors ${
                    selectedIndicator === indicator.id
                      ? 'bg-data-amber/10 border-l-2 border-data-amber'
                      : 'hover:bg-terminal-bg/50 border-l-2 border-transparent'
                  }`}
                >
                  <span
                    className={`text-[9px] truncate ${
                      selectedIndicator === indicator.id ? 'text-data-amber' : 'text-gray-500'
                    }`}
                  >
                    {indicator.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const CategoryNavigator: React.FC<CategoryNavigatorProps> = ({
  selectedIndicator,
  onIndicatorSelect,
  onCategorySelect,
  searchQuery = '',
  onSearchChange,
}) => {
  // Track expanded categories
  const [expandedCategories, setExpandedCategories] = useState<Set<IndicatorCategory>>(
    () => new Set<IndicatorCategory>(['gdp_growth', 'prices']) // Default expanded
  );

  const toggleCategory = useCallback((categoryId: IndicatorCategory) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
    onCategorySelect?.(categoryId);
  }, [onCategorySelect]);

  // Filter indicators based on search
  const filterIndicators = (indicators: IndicatorMetadata[]): IndicatorMetadata[] => {
    if (!searchQuery) return indicators;
    const query = searchQuery.toLowerCase();
    return indicators.filter(
      ind =>
        ind.name.toLowerCase().includes(query) ||
        ind.description.toLowerCase().includes(query) ||
        ind.id.toLowerCase().includes(query)
    );
  };

  // Expand all categories if searching
  const effectiveExpanded = searchQuery
    ? new Set(CATEGORIES.map(c => c.id))
    : expandedCategories;

  return (
    <div className="h-full flex flex-col bg-terminal-panel border-r border-terminal-border">
      {/* Search Header */}
      <div className="p-2 border-b border-terminal-border">
        <div className="relative">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            type="text"
            placeholder="Search indicators..."
            value={searchQuery}
            onChange={e => onSearchChange?.(e.target.value)}
            className="w-full bg-terminal-bg border border-terminal-border rounded px-7 py-1.5 text-[10px] text-gray-300 placeholder-gray-600 focus:outline-none focus:border-data-amber/50"
          />
        </div>
      </div>

      {/* Category List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {CATEGORIES.map(category => {
          const indicators = filterIndicators(getIndicatorsByCategory(category.id));
          // Hide categories with no matching indicators when searching
          if (searchQuery && indicators.length === 0) return null;

          return (
            <CategoryItem
              key={category.id}
              category={category}
              indicators={indicators}
              isExpanded={effectiveExpanded.has(category.id)}
              selectedIndicator={selectedIndicator}
              onToggle={() => toggleCategory(category.id)}
              onIndicatorSelect={onIndicatorSelect}
            />
          );
        })}
      </div>

      {/* Footer Stats */}
      <div className="p-2 border-t border-terminal-border bg-terminal-header">
        <div className="flex items-center justify-between text-[9px] text-gray-500">
          <span>{CATEGORIES.length} categories</span>
          <span>200+ indicators</span>
        </div>
      </div>
    </div>
  );
};

export default CategoryNavigator;
